import React, { useState, useMemo, useCallback } from "react";
import { ChevronDownIcon, SearchIcon, PlusIcon } from "@heroicons/react/solid";
import { useMutation, useQueryClient } from "react-query";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from "../../../../api";
import { classNames } from "./../../../misc/styling";
import View from "./View";
import AddNewView from "./AddNewView";
import FadeIn from "../../../transitions/FadeIn";

const ViewsList = ({ selectedViewId, setSelectedViewId, allViews = [], smartTableId }) => {
  const queryClient = useQueryClient();
  const smartTableApi = new ApiRequest(API_ROUTES.SMART_TABLE_VIEWS, API_AUTH_TYPES.ADVISOR);

  // Local state for optimistic updates
  const [optimisticViews, setOptimisticViews] = useState(allViews);
  const [showAddView, setShowAddView] = useState(false);
  const [sourceView, setSourceView] = useState(null); // For copying views
  const [showPersonalViews, setShowPersonalViews] = useState(true);
  const [showColaborativeViews, setShowColaborativeViews] = useState(true);
  const [query, setQuery] = useState("");

  // Update optimistic state when prop changes
  React.useEffect(() => {
    setOptimisticViews(allViews);
  }, [allViews]);

  // Filter views based on search query
  const filteredViews = useMemo(() => {
    const viewsToFilter = optimisticViews.length > 0 ? optimisticViews : allViews;
    return query === ""
      ? viewsToFilter
      : viewsToFilter.filter((view) =>
        view.name.toLowerCase().includes(query.toLowerCase())
      );
  }, [optimisticViews, allViews, query]);

  const myViews = filteredViews.filter((view) => view.advisor_id);
  const colaborativeViews = filteredViews.filter((view) => !view.advisor_id);

  // Create new view mutation with optimistic updates
  const createViewMutation = useMutation(
    ({ name, whoCanEdit }) =>
      smartTableApi.post({
        endpoint: 'create',
        body: {
          name: name || "New View Name",
          who_can_edit: whoCanEdit,
          smart_table_id: smartTableId
        }
      }),
    {
      onMutate: async ({ name, whoCanEdit }) => {
        await queryClient.cancelQueries('smart-table');

        const optimisticView = {
          smart_table_view_id: `temp-${Date.now()}`,
          name: name || "New View Name",
          who_can_edit: whoCanEdit,
          advisor_id: whoCanEdit === 'personal' ? 'current-user' : null,
          isOptimistic: true,
          createdAt: new Date().toISOString()
        };

        const previousViews = optimisticViews;
        setOptimisticViews(prev => [...prev, optimisticView]);

        return { previousViews, optimisticView };
      },
      onError: (error, variables, context) => {
        setOptimisticViews(context.previousViews);
        console.error("Error creating view:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["smart-table-complete-data", { tableId: smartTableId }]);
        setShowAddView(false);
      }
    }
  );

  // Update view mutation with optimistic updates
  const updateViewMutation = useMutation(
    ({ viewId, updates }) =>
      smartTableApi.patch({
        endpoint: 'update',
        body: {
          ...updates,
          id: viewId,
        }
      }),
    {
      onMutate: async ({ viewId, updates }) => {
        await queryClient.cancelQueries('smart-table');

        const previousViews = optimisticViews;
        setOptimisticViews(prev =>
          prev.map(view =>
            view.smart_table_view_id === viewId
              ? { ...view, ...updates }
              : view
          )
        );

        return { previousViews };
      },
      onError: (error, variables, context) => {
        setOptimisticViews(context.previousViews);
        console.error("Error updating view:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["smart-table-complete-data", { tableId: smartTableId }]);
      }
    }
  );

  // Delete view mutation with optimistic updates
  const deleteViewMutation = useMutation(
    (viewId) =>
      smartTableApi.delete({
        endpoint: 'delete',
        params: [viewId]
      }),
    {
      onMutate: async (viewId) => {
        await queryClient.cancelQueries('smart-table');

        const previousViews = optimisticViews;
        setOptimisticViews(prev =>
          prev.filter(view => view.smart_table_view_id !== viewId)
        );

        return { previousViews };
      },
      onError: (error, variables, context) => {
        setOptimisticViews(context.previousViews);
        console.error("Error deleting view:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["smart-table-complete-data", { tableId: smartTableId }]);
      }
    }
  );

  // Reorder views mutation with optimistic updates
  const reorderViewsMutation = useMutation(
    ({ views, section, smartTableId }) =>
      smartTableApi.patch({
        endpoint: 'reorder',
        body: {
          views: views.map((view, index) => ({
            id: view.smart_table_view_id,
            order: index + 1
          })),
          section,
          smart_table_id: smartTableId
        }
      }),
    {
      onMutate: async ({ views, section }) => {
        await queryClient.cancelQueries('smart-table');

        const previousViews = optimisticViews;
        setOptimisticViews(views);

        return { previousViews };
      },
      onError: (error, variables, context) => {
        setOptimisticViews(context.previousViews);
        console.error("Error reordering views:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["smart-table-complete-data", { tableId: smartTableId }]);
      }
    }
  );

  // Copy view mutation with optimistic updates
  const copyViewMutation = useMutation(
    ({ name, advisor_id, smart_table_id, source_view_id, filters, sorts, hidden_columns, column_order, column_widths }) =>
      smartTableApi.post({
        endpoint: 'create',
        body: {
          name,
          who_can_edit: advisor_id ? "personal" : "colaborative",
          smart_table_id,
          source_view_id,
          filters,
          sorts,
          hidden_columns,
          column_order,
          column_widths
        }
      }),
    {
      onMutate: async ({ name, advisor_id }) => {
        await queryClient.cancelQueries('smart-table');

        const optimisticView = {
          smart_table_view_id: `temp-${Date.now()}`,
          name,
          advisor_id,
          isOptimistic: true,
          createdAt: new Date().toISOString()
        };

        const previousViews = optimisticViews;
        setOptimisticViews(prev => [...prev, optimisticView]);

        return { previousViews, optimisticView };
      },
      onError: (error, variables, context) => {
        setOptimisticViews(context.previousViews);
        console.error("Error copying view:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries(["smart-table-complete-data", { tableId: smartTableId }]);
        setShowAddView(false);
        setSourceView(null);
      }
    }
  );

  // Handlers
  const handleCreateView = useCallback((name, whoCanEdit, copyFromView = null) => {
    const mutation = copyFromView ? copyViewMutation : createViewMutation;

    const newViewData = {
      name,
      advisor_id: whoCanEdit === "personal" ? "advisor" : null,
      smart_table_id: smartTableId,
    };

    // If copying, include the source view data
    if (copyFromView) {
      newViewData.source_view_id = copyFromView.smart_table_view_id;
      newViewData.filters = copyFromView.filters || [];
      newViewData.sorts = copyFromView.sorts || [];
      newViewData.hidden_columns = copyFromView.hidden_columns || [];
      newViewData.column_order = copyFromView.column_order || [];
      newViewData.column_widths = copyFromView.column_widths || {};
    }

    mutation.mutate(newViewData);
  }, [createViewMutation, copyViewMutation, smartTableId]);

  // Updated copy handler for when copying from existing view
  const handleCopyView = useCallback((view) => {
    setSourceView(view);
    setShowAddView(true);
  }, []);

  // Updated create new view handler
  const handleCreateNewView = useCallback(() => {
    setSourceView(null);
    setShowAddView(true);
  }, []);

  // Handle modal close to reset sourceView
  const handleCloseAddView = useCallback((open) => {
    setShowAddView(open);
    if (!open) {
      setSourceView(null);
    }
  }, []);

  const handleUpdateView = useCallback((viewId, updates) => {
    updateViewMutation.mutate({ viewId, updates });
  }, [updateViewMutation]);

  const handleDeleteView = useCallback((viewId) => {
    if (optimisticViews.length <= 1) return; // Prevent deleting last view
    deleteViewMutation.mutate(viewId);
  }, [deleteViewMutation, optimisticViews.length]);

  // Reorder views with server persistence
  const handleMoveView = useCallback((dragIndex, hoverIndex, section) => {
    const viewsToReorder = section === 'personal' ? myViews : colaborativeViews;
    const reorderedViews = [...viewsToReorder];
    const draggedView = reorderedViews[dragIndex];

    // Perform the reordering
    reorderedViews.splice(dragIndex, 1);
    reorderedViews.splice(hoverIndex, 0, draggedView);

    // Create new optimistic views array maintaining proper order
    let newOptimisticViews;

    if (section === 'personal') {
      // Personal views come first, then collaborative
      newOptimisticViews = [...reorderedViews, ...colaborativeViews];
    } else {
      // Personal views first, then reordered collaborative views
      newOptimisticViews = [...myViews, ...reorderedViews];
    }

    setOptimisticViews(newOptimisticViews);

    // Persist to server
    reorderViewsMutation.mutate({
      views: reorderedViews,
      section,
      smartTableId
    });
  }, [myViews, colaborativeViews, reorderViewsMutation, smartTableId]);

  const clearSearch = () => setQuery('');

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col w-full max-w-full h-full">
        {/* Header with Search and Create Button */}
        <div className="flex-shrink-0 border-b border-gray-200 pb-2 mb-2">
          {/* Search */}
          <div className="flex text-gray-500 items-center py-1 mb-1">
            <SearchIcon className="h-4 w-4 flex-shrink-0 mr-2" />
            <input
              type="text"
              className="ring-0 focus:ring-0 border-0 flex-1 min-w-0 text-sm bg-transparent"
              placeholder="Find a view"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
            {query && (
              <button
                onClick={clearSearch}
                className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Create New View Button */}
          <div className="flex justify-between items-center h-8">
            <h2 className="text-sm font-semibold text-gray-900">Views</h2>
            <button
              onClick={handleCreateNewView}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-150"
              title="Create new view"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Views Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Personal Views */}
          <button
            onClick={() => setShowPersonalViews(!showPersonalViews)}
            className="font-semibold text-gray-800 flex justify-between items-center w-full py-1 px-1 mb-2"
          >
            <p className="text-sm">Personal Views</p>
            <ChevronDownIcon
              className={classNames(
                showPersonalViews && "rotate-180",
                "transition duration-100 h-4 w-4 text-gray-800"
              )}
            />
          </button>

          <FadeIn isShowing={showPersonalViews}>
            {myViews?.length > 0 ? (
              <ul className="pb-4 space-y-1">
                {myViews.map((view, index) => (
                  <li key={view.smart_table_view_id} className="w-full">
                    <View
                      view={view}
                      setSelectedViewId={setSelectedViewId}
                      selectedViewId={selectedViewId}
                      thisViewIndex={index}
                      section="personal"
                      isColaborativeViews={false}
                      myViewsLength={myViews?.length || 0}
                      allViewsLength={optimisticViews?.length || 0}
                      onUpdateView={handleUpdateView}
                      onDeleteView={handleDeleteView}
                      onMoveView={handleMoveView}
                      onCopyView={handleCopyView}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex px-2 py-4">
                <p className="text-gray-500 text-sm">
                  {query ? "No matching views" : "No personal views"}
                </p>
              </div>
            )}
          </FadeIn>

          {/* Collaborative Views */}
          <button
            onClick={() => setShowColaborativeViews(!showColaborativeViews)}
            className="font-semibold text-gray-800 flex justify-between items-center w-full py-1 px-1 mb-2"
          >
            <p className="text-sm">Collaborative Views</p>
            <ChevronDownIcon
              className={classNames(
                showColaborativeViews && "rotate-180",
                "transition duration-100 h-4 w-4 text-gray-800"
              )}
            />
          </button>

          <FadeIn isShowing={showColaborativeViews}>
            {colaborativeViews?.length > 0 ? (
              <ul className="pb-4 space-y-1">
                {colaborativeViews.map((view, index) => (
                  <li key={view.smart_table_view_id} className="w-full">
                    <View
                      view={view}
                      setSelectedViewId={setSelectedViewId}
                      selectedViewId={selectedViewId}
                      thisViewIndex={index}
                      section="collaborative"
                      isColaborativeViews={true}
                      myViewsLength={myViews?.length || 0}
                      allViewsLength={optimisticViews?.length || 0}
                      onUpdateView={handleUpdateView}
                      onDeleteView={handleDeleteView}
                      onMoveView={handleMoveView}
                      onCopyView={handleCopyView}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex px-2 py-4">
                <p className="text-gray-500 text-sm">
                  {query ? "No matching views" : "No collaborative views"}
                </p>
              </div>
            )}
          </FadeIn>
        </div>
      </div>

      <AddNewView
        showAddView={showAddView}
        setShowAddView={handleCloseAddView}
        smartTableId={smartTableId}
        onCreateView={handleCreateView}
        isCreating={createViewMutation.isLoading || copyViewMutation.isLoading}
        sourceView={sourceView}
      />
    </DndProvider>
  );
};

export default ViewsList;
